import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/level-sections/LevelSectionsAction";
import * as uiHelpers from "../LevelSectionsUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useLevelSectionsUIContext } from "../LevelSectionsUIContext";

export function LevelSectionsTable({ 
  deleteLevelSections
}) {
  // Level Sections UI Context
  const levelSectionsUIContext = useLevelSectionsUIContext();
  const levelSectionsUIProps = useMemo(() => {
    return {
      ids: levelSectionsUIContext.ids,
      setIds: levelSectionsUIContext.setIds,
      queryParams: levelSectionsUIContext.queryParams,
      setQueryParams: levelSectionsUIContext.setQueryParams,
      openEditLevelSectionPage: levelSectionsUIContext.openEditLevelSectionPage
    };
  }, [levelSectionsUIContext]);

  // Getting curret state of Level Sections list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.levelSections }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // Level Sections Redux state
  const dispatch = useDispatch();

  useEffect(() => {    
    levelSectionsUIProps.setIds([]);
    dispatch(actions.getLevelSections(levelSectionsUIProps.queryParams));
    return () => dispatch(actions.cleanLevelSectionsState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelSectionsUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "gradeLevelName",
      text: "Sınıf Düzeyi",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "grade",
      text: "Kur Sınıfı",
      sort: true,
      sortCaret: sortCaret,
    },    
    {
      dataField: "name",
      text: "Kur",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "capacity",
      text: "Kapasite",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "courseName",
      text: "Ders",
      sort: true,
      sortCaret: sortCaret,
    }
  ];

  const actionColumn = {
    dataField: "action",
    text: "İşlemler",
    formatter: columnFormatters.ActionsColumnFormatter,
    formatExtraData: {
      isEditionAuthorized,
      isDeletionAuthorized,
      openEditLevelSectionPage: levelSectionsUIProps.openEditLevelSectionPage,
      deleteLevelSections
    },
    classes: "text-right pr-0",
    headerClasses: "text-right pr-3",
    style: {
      minWidth: "100px",
    }
  }

  if (isEditionAuthorized || isDeletionAuthorized) columns.push(actionColumn);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: levelSectionsUIProps.queryParams.pageSize,
    page: levelSectionsUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ? entities : [] }
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                levelSectionsUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: levelSectionsUIProps.ids,
                setIds: levelSectionsUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}